import { AnalyticsBrowser } from '@segment/analytics-next';
import type { Traits, EventProperties } from '@segment/analytics-next';
import { AnalyticsConnectionType, UserSession } from '../types';

// @ts-ignore templated via Vite
const analyticsKey = DEFINE_SEGMENT_KEY;

type AppVersionTraits = {
  web_version?: string;
  browser_extension_version?: string;
};
export const userTraits = (
  user: Partial<UserSession> | null,
  appVersions: AppVersionTraits,
): Traits | undefined => {
  if (!user) {
    return undefined;
  }

  const traits: Traits = {
    id: user.id,
    first_name: user.first_name,
    last_name: user.last_name,
    username: user.username,
    email: user.email,
    name: `${user.first_name} ${user.last_name}`.trim(),
    ...(appVersions || {}),
  };

  if (window?.app?.isElectron && window?.desktopIntegrations) {
    traits.desktop_version = window?.desktopIntegrations.appInfo.version;
  }

  return traits;
};

const analytics = analyticsKey ? AnalyticsBrowser.load({ writeKey: analyticsKey }) : null;

function identify(user?: UserSession, traits?: Traits): void {
  if (!analytics) {
    return;
  }
  if (!user) {
    analytics.reset();
    return;
  }

  analytics.identify(`${user.assistant_id ?? user.id}`, traits);
}

/**
 * Used for segment-based metrics
 */
function segment(event: string, properties: EventProperties = {}): void {
  if (!analytics) {
    return;
  }

  try {
    analytics.track(event, properties);
  } catch (e) {
    console.warn(e);
  }
}

function screen(screen: string): void {
  if (!analytics) {
    return;
  }
  analytics.page(undefined, screen);
}

/**
 * Used for google and plausible analytics
 */
function trackEvent(name: string, type?: string, properties?: Record<string, any>): void {
  if (window.gtag) {
    window.gtag('event', type, {
      event_category: name,
      ...properties,
    });
  }

  if (window.plausible) {
    window.plausible(name, {
      props: {
        action: type,
        ...properties,
      },
    });
  }
}

function trackStartPage(event: 'welcome' | 'download', properties?: Record<string, any>): void {
  const eventName = event === 'welcome' ? 'Welcome to Clay Visited' : 'Download Button Tapped';
  trackEvent(eventName, properties?.type, properties);
}

function trackConnectAccount(type: AnalyticsConnectionType): void {
  trackEvent('Account Connected', type);
}

function trackReferral(accepted: boolean): void {
  if (accepted) {
    trackEvent('Referral Accepted');
  } else {
    trackEvent('Referral Skipped');
  }
}

function trackProfileCreated(): void {
  trackEvent('Clay Profile Created');
}

function googleTrackConversion(): void {
  if (window.gtag) {
    window.gtag('event', 'conversion', {
      send_to: 'AW-10937739734/uzC1CMG18-MDENbTwt8o',
    });
  }
}

export default {
  identify,
  track: segment,
  trackStartPage,
  trackConnectAccount,
  trackReferral,
  trackProfileCreated,
  screen,
  googleTrackConversion,
};
